import { useCallback, useRef } from "react";
import { trackEvent } from "@/analytics";
import { FORM_INTERACTION_EVENTS } from "@/analytics/events";

interface FormTrackingOptions {
  formName: string;
  debounceMs?: number;
}

interface FieldTrackingData {
  fieldName: string;
  fieldType?: string;
  errorType?: string;
  errorMessage?: string;
  validationPass?: boolean;
}

/**
 * Hook to automatically track form interactions.
 * Prevents duplicate tracking for the same field and debounces change events.
 *
 * @param options - Form name and optional debounce duration
 *
 * @example
 * const formTracking = useFormTracking({ formName: "tax_settings" });
 *
 * <input
 *   onFocus={() => formTracking.trackFieldFocus("jurisdiction", "select")}
 *   onChange={(e) => formTracking.trackFieldChange("jurisdiction", "select")}
 *   onBlur={(e) => {
 *     if (error) {
 *       formTracking.trackValidationFailed("jurisdiction", "required", "Field is required");
 *     }
 *   }}
 * />
 */
export const useFormTracking = ({ formName, debounceMs = 500 }: FormTrackingOptions) => {
  const focusedFieldsRef = useRef<Set<string>>(new Set());
  const changedFieldsRef = useRef<Set<string>>(new Set());
  const changeTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  /**
   * Track when a field receives focus (fires only once per field)
   */
  const trackFieldFocus = useCallback((fieldName: string, fieldType?: string) => {
    // Only track first focus for each field
    if (focusedFieldsRef.current.has(fieldName)) {
      return;
    }

    focusedFieldsRef.current.add(fieldName);

    trackEvent(FORM_INTERACTION_EVENTS.FORM_FIELD_FOCUSED, {
      form_name: formName,
      field_name: fieldName,
      field_type: fieldType,
      source: "web",
    });
  }, [formName]);

  /**
   * Track when a field value changes (debounced to prevent spam)
   */
  const trackFieldChange = useCallback((fieldName: string, fieldType?: string) => {
    // Clear existing timeout for this field
    const existingTimeout = changeTimeoutsRef.current.get(fieldName);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set new debounced timeout
    const timeout = setTimeout(() => {
      // Only track first change for each field
      if (changedFieldsRef.current.has(fieldName)) {
        return;
      }

      changedFieldsRef.current.add(fieldName);

      trackEvent(FORM_INTERACTION_EVENTS.FORM_FIELD_CHANGED, {
        form_name: formName,
        field_name: fieldName,
        field_type: fieldType,
        source: "web",
      });

      changeTimeoutsRef.current.delete(fieldName);
    }, debounceMs);

    changeTimeoutsRef.current.set(fieldName, timeout);
  }, [formName, debounceMs]);

  /**
   * Track validation failures
   */
  const trackValidationFailed = useCallback((
    fieldName: string,
    errorType: string,
    errorMessage?: string,
    fieldType?: string
  ) => {
    trackEvent(FORM_INTERACTION_EVENTS.FORM_VALIDATION_FAILED, {
      form_name: formName,
      field_name: fieldName,
      error_type: errorType,
      error_message: errorMessage,
      validation_pass: false,
      field_type: fieldType,
      source: "web",
    });
  }, [formName]);

  /**
   * Track successful form submission
   */
  const trackFormSubmit = useCallback((additionalProperties?: Record<string, any>) => {
    trackEvent(FORM_INTERACTION_EVENTS.FORM_SUBMITTED, {
      form_name: formName,
      source: "web",
      ...additionalProperties,
    });
  }, [formName]);

  /**
   * Reset tracking state (useful for multi-step forms or when form is reset)
   */
  const resetTracking = useCallback(() => {
    focusedFieldsRef.current.clear();
    changedFieldsRef.current.clear();

    // Clear all pending timeouts
    changeTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    changeTimeoutsRef.current.clear();
  }, []);

  return {
    trackFieldFocus,
    trackFieldChange,
    trackValidationFailed,
    trackFormSubmit,
    resetTracking,
  };
};
